import React, { Suspense, lazy } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from "react-router-dom";

// COMPONENTS
import ScrollToTop from "./components/ScrollToTop";

const Navbar = lazy(() => import("./components/Navbar/Navbar"));
const NavbarEn = lazy(() => import("./components/Navbar/NavbarEn"));
const NavbarFr = lazy(() => import("./components/Navbar/NavbarFr"));
const NavbarDE = lazy(() => import("./components/Navbar/NavbarDE"));

const Home = lazy(() => import("./components/pages/Home"));
const HomeDE = lazy(() => import("./components/pages/HomeDE"));
const HomeEn = lazy(() => import("./components/pages/HomeEn"));
const HomeFr = lazy(() => import("./components/pages/HomeFr"));

const Empresa = lazy(() => import("./components/pages/Empresa"));
const Products = lazy(() => import("./components/pages/Products"));
const Services = lazy(() => import("./components/pages/Services"));
const ContactPage = lazy(() => import("./components/pages/ContactPage"));
const Noticias = lazy(() => import("./components/pages/Noticias"));
const SignUp = lazy(() => import("./components/pages/SignUp"));
const Natal = lazy(() => import("./components/Postal/Natal"));
const Christmas = lazy(() => import("./components/Postal/Natal-EN"));
const Mailersend = lazy(() => import("./components/Mail/Mailersend"));
const PDFSender = lazy(() => import("./components/PDFCreate/Post"));
const Recrut = lazy(() => import("./components/Candidaturas/Recrut"));
const Emprego = lazy(() => import("./components/Emprego/Emprego"));
const CandidaturaEspontanea = lazy(() => import("./components/Emprego/CandidaturaEspontanea"));
const Upload = lazy(() => import("./components/Upload/UploadPage"));
const Sendfiles = lazy(() => import("./components/Sendfiles/Send"));
const AdminLogin = lazy(() => import("./components/Admin/AdminLogin"));
const Admin = lazy(() => import("./components/Admin/Admin"));
const TimelineConfig = lazy(() => import("./components/TimelineConfig/TimelineConfig"));
const CertificateConfig = lazy(() => import("./components/CertificateConfig/CertificateConfig"));
const NewsConfig = lazy(() => import("./components/NewsConfig/NewsConfig"));

const EmpresaEn = lazy(() => import("./components/pages/EmpresaEn"));
const ProductsEn = lazy(() => import("./components/pages/ProductsEn"));
const ServicesEn = lazy(() => import("./components/pages/ServicesEn"));
const ContactPageEn = lazy(() => import("./components/pages/ContactPageEn"));
const NoticiasEn = lazy(() => import("./components/pages/NoticiasEn"));

const EmpresaFr = lazy(() => import("./components/pages/EmpresaFr"));
const ProductsFr = lazy(() => import("./components/pages/ProductsFr"));
const ServicesFr = lazy(() => import("./components/pages/ServicesFr"));
const ContactPageFr = lazy(() => import("./components/pages/ContactPageFr"));
const NoticiasFr = lazy(() => import("./components/pages/NoticiasFr"));

const EmpresaDE = lazy(() => import("./components/pages/EmpresaDe"));
const ProductsDE = lazy(() => import("./components/pages/ProductsDe"));
const ServicesDE = lazy(() => import("./components/pages/ServicesDe"));
const ContactPageDE = lazy(() => import("./components/pages/ContactPageDe"));
const NoticiasDE = lazy(() => import("./components/pages/NoticiasDE"));

const ProtectedAdminRoute = ({ children }) => {
  const location = useLocation();
  const isAuthenticated =
    typeof window !== "undefined" &&
    window.sessionStorage.getItem("plastifa.admin.auth") === "true";

  if (!isAuthenticated) {
    return <Navigate to="/AdminLogin" replace state={{ from: location.pathname }} />;
  }

  return children;
};

function App() {
  return (
    <Router>
      <ScrollToTop />

      <Suspense fallback={null}>
      <Routes>

        {/* ---------------- PT ROUTES ---------------- */}
        <Route
          path="/"
          element={
            <>
              <Navbar />
              <Home />
            </>
          }
        />

        <Route
          path="/Empresa"
          element={
            <>
              <Navbar />
              <Empresa />
            </>
          }
        />

        <Route
          path="/products"
          element={
            <>
              <Navbar />
              <Products />
            </>
          }
        />

        <Route
          path="/services"
          element={
            <>
              <Navbar />
              <Services />
            </>
          }
        />

        <Route
          path="/ContactPage"
          element={
            <>
              <Navbar />
              <ContactPage />
            </>
          }
        />

        <Route
          path="/Noticias"
          element={
            <>
              <Navbar />
              <Noticias />
            </>
          }
        />

        <Route
          path="/sign-up"
          element={
            <>
              <Navbar />
              <SignUp />
            </>
          }
        />

        <Route
          path="/Natal"
          element={
            <>
              <Navbar />
              <Natal />
            </>
          }
        />

        <Route
          path="/Christmas"
          element={
            <>
              <Navbar />
              <Christmas />
            </>
          }
        />

        <Route
          path="/Mailersend"
          element={
            <>
              <Navbar />
              <Mailersend />
            </>
          }
        />

        <Route
          path="/PDFSender"
          element={
            <>
              <Navbar />
              <PDFSender />
            </>
          }
        />

        <Route
          path="/Recrut"
          element={
            <>
              <Navbar />
              <Recrut />
            </>
          }
        />

        <Route
          path="/Emprego"
          element={
            <>
              <Navbar />
              <Emprego />
            </>
          }
        />

        <Route
          path="/CandidaturaEspontanea"
          element={
            <>
              <Navbar />
              <CandidaturaEspontanea />
            </>
          }
        />

        <Route
          path="/UploadPage"
          element={
            <>
              <Navbar />
              <Upload />
            </>
          }
        />

        <Route
          path="/Sendfiles"
          element={
            <>
              <Navbar />
              <Sendfiles />
            </>
          }
        />

        <Route
          path="/AdminLogin"
          element={
            <AdminLogin />
          }
        />

        <Route
          path="/Admin"
          element={
            <ProtectedAdminRoute>
              <Admin />
            </ProtectedAdminRoute>
          }
        />

        <Route
          path="/TimelineConfig"
          element={
            <ProtectedAdminRoute>
              <TimelineConfig />
            </ProtectedAdminRoute>
          }
        />

        <Route
          path="/CertificateConfig"
          element={
            <ProtectedAdminRoute>
              <CertificateConfig />
            </ProtectedAdminRoute>
          }
        />

        <Route
          path="/NewsConfig"
          element={
            <ProtectedAdminRoute>
              <NewsConfig />
            </ProtectedAdminRoute>
          }
        />

        {/* ---------------- EN ROUTES ---------------- */}
        <Route
          path="/HomeEn"
          element={
            <>
              <NavbarEn />
              <HomeEn />
            </>
          }
        />

        <Route
          path="/EmpresaEn"
          element={
            <>
              <NavbarEn />
              <EmpresaEn />
            </>
          }
        />

        <Route
          path="/productsen"
          element={
            <>
              <NavbarEn />
              <ProductsEn />
            </>
          }
        />

        <Route
          path="/servicesEn"
          element={
            <>
              <NavbarEn />
              <ServicesEn />
            </>
          }
        />

        <Route
          path="/ContactPageen"
          element={
            <>
              <NavbarEn />
              <ContactPageEn />
            </>
          }
        />

        <Route
          path="/NoticiasEn"
          element={
            <>
              <NavbarEn />
              <NoticiasEn />
            </>
          }
        />

        {/* ---------------- FR ROUTES ---------------- */}
        <Route
          path="/HomeFr"
          element={
            <>
              <NavbarFr />
              <HomeFr />
            </>
          }
        />

        <Route
          path="/EmpresaFr"
          element={
            <>
              <NavbarFr />
              <EmpresaFr />
            </>
          }
        />

        <Route
          path="/productsFr"
          element={
            <>
              <NavbarFr />
              <ProductsFr />
            </>
          }
        />

        <Route
          path="/servicesFr"
          element={
            <>
              <NavbarFr />
              <ServicesFr />
            </>
          }
        />

        <Route
          path="/ContactPageFr"
          element={
            <>
              <NavbarFr />
              <ContactPageFr />
            </>
          }
        />

        <Route
          path="/NoticiasFr"
          element={
            <>
              <NavbarFr />
              <NoticiasFr />
            </>
          }
        />

        {/* ---------------- DE ROUTES ---------------- */}
        <Route
          path="/HomeDE"
          element={
            <>
              <NavbarDE />
              <HomeDE />
            </>
          }
        />

        <Route
          path="/EmpresaDE"
          element={
            <>
              <NavbarDE />
              <EmpresaDE />
            </>
          }
        />

        <Route
          path="/productsDE"
          element={
            <>
              <NavbarDE />
              <ProductsDE />
            </>
          }
        />

        <Route
          path="/servicesDE"
          element={
            <>
              <NavbarDE />
              <ServicesDE />
            </>
          }
        />

        <Route
          path="/ContactPageDE"
          element={
            <>
              <NavbarDE />
              <ContactPageDE />
            </>
          }
        />

        <Route
          path="/NoticiasDE"
          element={
            <>
              <NavbarDE />
              <NoticiasDE />
            </>
          }
        />

        {/* ROTA INEXISTENTE → Redireciona para Home */}
        <Route path="*" element={<Navigate to="/" />} />
      </Routes>
      </Suspense>
    </Router>
  );
}

export default App;
